/**
 * «Pídanoslo»: registrar lo que alguien necesita y no tenemos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ EXISTE. Una lista vacía era el final del camino: quien buscaba
 * "estantería 2 metros" y no la encontraba se iba, y la única huella de esa
 * demanda quedaba en su cabeza. Este formulario convierte el abandono en un
 * dato, y el dato agrupado es lo que le dice al equipo qué agregar al catálogo
 * con números en vez de con intuición.
 *
 * APARECE EN DOS SITUACIONES, y la segunda es la que más demanda captura:
 *
 *  · Cuando la búsqueda no dio NADA. Es el caso obvio.
 *  · Cuando dio resultados pero NINGUNO SIRVE. Quien busca "teclado" encuentra
 *    tres y necesita uno lavable de silicona: la búsqueda «funcionó», así que
 *    antes no se le ofrecía nada y esa demanda —la más valiosa, porque es
 *    específica— se perdía entera. Ahí el catálogo pinta una tarjeta más junto a
 *    los resultados.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tres decisiones de forma que no son de gusto:
 *
 *  1. VIENE PRECARGADO con lo que la persona ya escribió. Pedirle que lo vuelva
 *     a tipear es la manera más segura de que no lo mande: ya hizo el esfuerzo
 *     de buscar y no encontró nada, y ese es el peor momento para pedirle
 *     trabajo.
 *  2. LA CANTIDAD ES OPCIONAL y lo dice. La mayoría de esta cola son avisos
 *     —"me falta esto"—, no pedidos; exigir una cifra convertiría un aviso de
 *     diez segundos en una decisión de compra que quien mira el catálogo
 *     todavía no tomó.
 *  3. NO PROMETE NADA. El acuse dice que quedó registrado y que si se cotiza lo
 *     contacta una persona. Un "se lo conseguimos" que el sistema no puede
 *     cumplir es peor que no ofrecer el formulario.
 *
 * El `clienteId` NO se manda: lo saca el backend del token. La faena viaja solo
 * si el carrito ya tiene una en contexto, y una faena ajena responde 404, así
 * que acá no hay nada que validar sobre pertenencia.
 */
import { useEffect, useId, useState } from 'react'
import { LARGO_MAXIMO_BUSQUEDA, LARGO_MAXIMO_DETALLE } from '@compranorte/contracts'
import { useCrearSolicitud } from '../api/hooks'
import { Aviso, Campo } from './ui'

/** Mínimo del contrato: bajo esto el backend responde 400. */
const MINIMO_TEXTO = 3

export function PedirAlCatalogo({
  textoBuscado,
  faenaId,
  iniciarAbierto = false,
}: {
  /** Lo que la persona escribió en el buscador. Puede venir vacío. */
  textoBuscado: string
  /** Faena en contexto del carrito, si hay una. */
  faenaId: string | null
  /**
   * Empieza con el formulario desplegado, sin el botón «Pídanoslo».
   *
   * Lo usa la tarjeta «personalizado» del catálogo: ahí el clic de abrir YA
   * ocurrió sobre la tarjeta, y volver a pedirlo sobre un botón idéntico sería
   * hacer el mismo gesto dos veces para llegar al mismo formulario.
   */
  iniciarAbierto?: boolean
}) {
  const idBase = useId()
  const crear = useCrearSolicitud()

  const [abierto, setAbierto] = useState(iniciarAbierto)
  const [texto, setTexto] = useState(textoBuscado)
  const [detalle, setDetalle] = useState('')
  const [cantidad, setCantidad] = useState('')
  const [enviado, setEnviado] = useState<string | null>(null)

  /*
   * Si la persona sigue tecleando en el buscador, el campo se pone al día
   * mientras no lo haya editado a mano —eso es lo que significa el formulario
   * cerrado—. Con el formulario abierto NO se toca: sobreescribir lo que alguien
   * está escribiendo es la peor clase de "ayuda".
   */
  useEffect(() => {
    if (!abierto) setTexto(textoBuscado)
  }, [textoBuscado, abierto])

  /*
   * Otra búsqueda es otra demanda posible, así que el acuse de la anterior no
   * puede quedar tapando el formulario: si no se limpiara, quien pide una cosa y
   * después busca otra que tampoco está se encontraría con el "queda
   * registrado" de la primera y no tendría forma de pedir la segunda.
   *
   * Se compara con lo que se ENVIÓ, no con un booleano: buscar exactamente lo
   * mismo otra vez debe seguir mostrando el acuse, para no invitar a pedirlo dos
   * veces. La comparación va sobre el texto con los espacios colapsados, que es
   * la forma en que el servidor lo guardó: si no, escribir dos espacios en el
   * buscador se leería como otra búsqueda y borraría el acuse.
   */
  useEffect(() => {
    const buscado = textoBuscado.trim().replace(/\s+/g, ' ')
    setEnviado((previo) => (previo !== null && previo !== buscado ? null : previo))
  }, [textoBuscado])

  const limpio = texto.trim().replace(/\s+/g, ' ')
  const cantidadNumero = cantidad.trim() === '' ? null : Number(cantidad)
  const cantidadInvalida =
    cantidadNumero !== null &&
    (!Number.isInteger(cantidadNumero) || cantidadNumero < 1 || cantidadNumero > 1_000_000)

  const problemaTexto =
    limpio.length === 0
      ? null
      : limpio.length < MINIMO_TEXTO
        ? 'Escriba al menos tres caracteres.'
        : !/[\p{L}\p{N}]/u.test(limpio)
          ? 'Escriba con palabras qué necesita.'
          : null

  const puedeEnviar =
    limpio.length >= MINIMO_TEXTO && problemaTexto === null && !cantidadInvalida && !crear.isPending

  if (enviado !== null) {
    return (
      <Aviso tono="ok" titulo="Queda registrado">
        Anotamos <strong>{enviado}</strong> en nuestra lista de lo que nos piden y no tenemos.
        Cuando lo incorporemos aparecerá en el catálogo; si conviene cotizarlo antes, lo contacta
        una persona del equipo. La solicitud no compromete una compra.
      </Aviso>
    )
  }

  if (!abierto) {
    return (
      <div className="text-center">
        <button type="button" className="btn btn-accion" onClick={() => setAbierto(true)}>
          Pídanoslo
        </button>
        <p className="mt-1.5 text-[12px] text-acero-600">
          Si no está en el catálogo, díganos qué necesita: lo buscamos con nuestros proveedores.
        </p>
      </div>
    )
  }

  return (
    <form
      className="panel p-3 space-y-3 text-left max-w-xl mx-auto"
      onSubmit={(e) => {
        e.preventDefault()
        if (!puedeEnviar) return
        crear.mutate(
          {
            texto: limpio,
            ...(detalle.trim() ? { detalle: detalle.trim() } : {}),
            ...(cantidadNumero !== null ? { cantidad: cantidadNumero } : {}),
            ...(faenaId ? { faenaId } : {}),
          },
          {
            // El acuse se queda con lo que de verdad se guardó, que es lo que
            // devolvió el servidor y no lo que había en el campo. Y el
            // formulario se cierra: así el campo vuelve a seguir al buscador y
            // no queda un borrador viejo esperando a que alguien lo reenvíe.
            onSuccess: (s) => {
              setEnviado(s.busqueda)
              setAbierto(false)
              setDetalle('')
              setCantidad('')
            },
          },
        )
      }}
    >
      <div>
        <h3 className="text-[14px] font-semibold text-acero-900">Pídanos lo que no encontró</h3>
        <p className="text-[12.5px] text-acero-600 leading-relaxed mt-0.5">
          Lo anotamos y lo revisamos con nuestros proveedores. Mientras más gente pide lo mismo,
          antes lo incorporamos.
        </p>
      </div>

      {crear.isError && (
        <Aviso tono="error" titulo="No se pudo registrar">
          <span role="alert">{crear.error.message}</span>
        </Aviso>
      )}

      <Campo etiqueta="Qué necesita" requerido error={problemaTexto ?? undefined}>
        <input
          id={`${idBase}-texto`}
          className={`campo ${problemaTexto ? 'campo-error' : ''}`}
          value={texto}
          maxLength={LARGO_MAXIMO_BUSQUEDA}
          placeholder="Estantería metálica de 2 metros"
          autoFocus
          onChange={(e) => setTexto(e.target.value)}
        />
      </Campo>

      <Campo
        etiqueta="Detalle (opcional)"
        ayuda="Medidas, marca que le sirve, para qué equipo es. Nos ahorra una llamada."
      >
        <textarea
          id={`${idBase}-detalle`}
          className="campo min-h-[70px]"
          value={detalle}
          maxLength={LARGO_MAXIMO_DETALLE}
          onChange={(e) => setDetalle(e.target.value)}
        />
      </Campo>

      <div className="grid sm:grid-cols-2 gap-3 items-start">
        <Campo
          etiqueta="Cantidad estimada (opcional)"
          ayuda="Si todavía no lo sabe, déjelo en blanco."
          error={cantidadInvalida ? 'Debe ser un número entero de al menos 1.' : undefined}
        >
          <input
            id={`${idBase}-cantidad`}
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            className={`campo num w-28 ${cantidadInvalida ? 'campo-error' : ''}`}
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />
        </Campo>

        {/*
          La faena no se elige acá: es la que ya está en contexto del carrito. Un
          selector de faena en este formulario sería un tercer lugar donde
          cambiarla, y el destino de un despacho no se decide en una búsqueda
          fallida.
        */}
        <p className="text-[12px] text-acero-600 leading-relaxed sm:pt-5">
          {faenaId
            ? 'Lo asociamos a la faena que tiene seleccionada para el despacho.'
            : 'Sin faena seleccionada: si es para una faena en particular, cuéntenoslo en el detalle.'}
        </p>
      </div>

      <div className="flex gap-2 pt-0.5">
        <button type="submit" className="btn btn-accion" disabled={!puedeEnviar}>
          {crear.isPending ? 'Enviando…' : 'Enviar solicitud'}
        </button>
        <button
          type="button"
          className="btn btn-fantasma"
          disabled={crear.isPending}
          onClick={() => setAbierto(false)}
        >
          Cancelar
        </button>
      </div>
    </form>
  )
}
